import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  ApiKeySummary,
  fetchUserApiKey,
  fetchSessionPinningConfig,
  issueUserApiKey,
  ProviderApiKey,
  fetchProviderApiKeys,
  addProviderApiKey,
  deleteProviderApiKey,
  updateSessionPinningConfig,
} from "../../services/apiKeys";
import type { ProfileOutletContext } from "../ProfileLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Copy, Plus, RefreshCw, AlertTriangle, CheckCircle2, Trash2, Key } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const formatCurrency = (value: number | string | null | undefined) => {
  const amount = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number.isFinite(amount) ? amount : 0);
};

const formatDateTime = (value: string) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const ApiKeysPage: React.FC = () => {
  const { user } = useOutletContext<ProfileOutletContext>();
  // const [isLoading, setIsLoading] = useState(true);
  const [apiKey, setApiKey] = useState<ApiKeySummary | null>(null);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [isIssuing, setIsIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [sessionPinningEnabled, setSessionPinningEnabled] = useState(false);
  const [sessionPinningError, setSessionPinningError] = useState<string | null>(null);
  const [isSessionPinningSaving, setIsSessionPinningSaving] = useState(false);

  // Provider Keys State
  const [providerKeys, setProviderKeys] = useState<ProviderApiKey[]>([]);
  const [newProvider, setNewProvider] = useState("openai");
  const [newKey, setNewKey] = useState("");
  const [isAddingKey, setIsAddingKey] = useState(false);
  const [addKeyError, setAddKeyError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setRevealedToken(null);
      setError(null);

      try {
        const data = await fetchUserApiKey();

        try {
          const providerData = await fetchProviderApiKeys();
          if (mounted) {
            setProviderKeys(providerData.keys);
          }
        } catch (e) {
          console.error("Failed to fetch provider keys", e);
        }

        try {
          const sessionPinning = await fetchSessionPinningConfig();
          if (mounted) {
            setSessionPinningEnabled(sessionPinning.enabled);
          }
        } catch (e) {
          console.error("Failed to fetch session pinning config", e);
          if (mounted) {
            setSessionPinningError("Failed to load agent mode preference.");
          }
        }

        if (!mounted) {
          return;
        }

        setApiKey(data.hasKey ? data.key ?? null : null);
      } catch (err: any) {
        if (!mounted) {
          return;
        }

        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch key info.";
        setError(message);
      } finally {
        // if (mounted) setIsLoading(false);
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, []);

  const handleIssueKey = async () => {
    setIsIssuing(true);
    setError(null);
    setCopyStatus(null);

    try {
      const result = await issueUserApiKey();
      setApiKey(result.key);
      setRevealedToken(result.token);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create key. Try again.";
      setError(message);
    } finally {
      setIsIssuing(false);
    }
  };

  const handleSessionPinningChange = async (checked: boolean) => {
    const previousValue = sessionPinningEnabled;
    setSessionPinningEnabled(checked);
    setSessionPinningError(null);
    setIsSessionPinningSaving(true);

    try {
      const result = await updateSessionPinningConfig(checked);
      setSessionPinningEnabled(result.enabled);
    } catch (err: any) {
      setSessionPinningEnabled(previousValue);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update agent mode.";
      setSessionPinningError(message);
    } finally {
      setIsSessionPinningSaving(false);
    }
  };

  const handleAddProviderKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.trim()) return;

    setIsAddingKey(true);
    setAddKeyError(null);

    try {
      const res = await addProviderApiKey(newProvider, newKey);
      setProviderKeys([res.key, ...providerKeys]);
      setNewKey("");
    } catch (err: any) {
      setAddKeyError(err?.response?.data?.message || "Failed to add key");
    } finally {
      setIsAddingKey(false);
    }
  };

  const handleDeleteProviderKey = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this key?")) return;
    try {
      await deleteProviderApiKey(id);
      setProviderKeys(providerKeys.filter((k) => k.id !== id));
    } catch (err) {
      console.error("Failed to delete key", err);
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus("Copied!");
    } catch (err) {
      console.error("clipboard error", err);
      setCopyStatus("Failed");
    } finally {
      setTimeout(() => setCopyStatus(null), 2400);
    }
  };

  const helperMessage = useMemo(() => {
    return apiKey
      ? "Creatng a new key automatically revokes the previous secret. Access is allowed for only one key."
      : "Each organization gets one secret key. Create and save it in a secret manager.";
  }, [apiKey]);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">API Keys</h2>
          <p className="text-muted-foreground">
            Manage LLM API access. The secret is shown only once upon creation.
          </p>
        </div>
        <Button
          onClick={handleIssueKey}
          disabled={isIssuing}
          className="shadow-lg"
        >
          {isIssuing ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Creating...
            </>
          ) : apiKey ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4" /> Rotate Key
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" /> Create Secret Key
            </>
          )}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md border border-border/50">
        <span className="font-semibold text-foreground">Note: </span>
        {helperMessage}
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Agent Mode</CardTitle>
          <CardDescription>
            Keep a session pinned to the same routed model for multi-turn agent workflows.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-6 rounded-lg border p-4">
            <div className="space-y-1">
              <Label htmlFor="agent-mode-toggle">Agent Mode</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, SigLLM keeps your session sticky and only upgrades to a stronger model when needed.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {sessionPinningEnabled ? "On" : "Off"}
              </span>
              <Switch
                id="agent-mode-toggle"
                aria-label="Agent Mode"
                checked={sessionPinningEnabled}
                onCheckedChange={handleSessionPinningChange}
                disabled={isSessionPinningSaving}
              />
            </div>
          </div>
          {sessionPinningError && (
            <p className="text-sm text-destructive">{sessionPinningError}</p>
          )}
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      {revealedToken && (
        <Card className="border-emerald-500/50 bg-emerald-500/10 dark:bg-emerald-950/20">
          <CardHeader>
            <CardTitle className="text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              New Secret Key
            </CardTitle>
            <CardDescription className="text-emerald-600/80 dark:text-emerald-400/80">
              Save the key now — it won't be shown again. The old key was automatically revoked.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <code className="relative rounded bg-background px-[0.5rem] py-[0.3rem] font-mono text-sm font-semibold text-foreground border">
                {revealedToken}
              </code>
              <Button size="sm" variant="outline" onClick={() => handleCopy(revealedToken)}>
                <Copy className="mr-2 h-3.5 w-3.5" />
                {copyStatus || "Copy"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active Keys</CardTitle>
          <CardDescription>
            Your currently active API keys and their usage.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key Prefix</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-right">Usage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKey ? (
                  <TableRow>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.email}</span>
                        <span className="text-xs text-muted-foreground">Organization Key</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">{apiKey.token}</Badge>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleCopy(apiKey.token)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(user.balance)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDateTime(apiKey.createdAt)}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDateTime(apiKey.updatedAt)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{apiKey.usage} / {apiKey.rateLimit}</Badge>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                      No active keys found. Create one to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Add Provider Key</CardTitle>
            <CardDescription>
              Connect your own LLM provider accounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddProviderKey} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <select
                  id="provider"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={newProvider}
                  onChange={(e) => setNewProvider(e.target.value)}
                  disabled={isAddingKey}
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="gemini">Google Gemini</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="api-key">API Key</Label>
                <Input
                  id="api-key"
                  type="password"
                  placeholder="sk-..."
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  disabled={isAddingKey}
                  required
                />
              </div>

              {addKeyError && (
                <p className="text-sm text-destructive">{addKeyError}</p>
              )}

              <Button type="submit" disabled={isAddingKey || !newKey}>
                {isAddingKey ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Adding...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" /> Add Key
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Connected Providers</CardTitle>
            <CardDescription>
              Your stored encrypted keys.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {providerKeys.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No provider keys added.
              </div>
            ) : (
              <div className="space-y-4">
                {providerKeys.map((k) => (
                  <div key={k.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-muted rounded-full">
                        <Key className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium capitalize">{k.provider}</p>
                        <p className="text-xs text-muted-foreground font-mono">{k.keyMask}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteProviderKey(k.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div >
  );
};

export default ApiKeysPage;
